/**
 * tyt-blog-filter.js
 *
 * Client-side filter + sort + search for blog archive pages.
 *
 * Wires into the tyt-blog-control-bar section (Region dropdown, search input,
 * sort <select>) and operates on the tyt-blog-archive section's card grid.
 * Same look as the Trails collection filter, but everything is client-side —
 * no navigation.
 *
 * Attribute contract (set by the .liquid sections):
 *   [data-tyt-blog-filter-bar]                       the filter bar wrapper
 *   [data-tyt-cdd] / [data-tyt-cdd-toggle] / [data-tyt-cdd-menu]   region dropdown
 *   [data-tyt-blog-region-option][data-region="…"]   a region choice (client-side)
 *   [data-tyt-blog-region-value]                     the displayed region label
 *   [data-tyt-blog-search-input]                     the search <input>
 *   [data-tyt-blog-sort-select]                      the sort <select>
 *   [data-tyt-blog-grid]                             the card grid container
 *   [data-tyt-adv-card]                              each card
 *     - data-region="…"  data-published-time="<unix>"  data-title="…"  data-search-text="…"
 *   [data-tyt-blog-visible-count]                    count display in the header
 *   [data-tyt-blog-current-sort]                     current sort display
 *   [data-tyt-blog-no-results]                       empty-state placeholder
 *   [data-tyt-blog-clear-filters]                    clear-filters button
 *
 * Region: 'all' (the reset option) applies no filter; otherwise the card's
 * data-region must === the chosen value (normalised lowercase).
 * Search is debounced ~60ms, AND-matched across whitespace tokens.
 * Sort is DOM reordering: newest/oldest by data-published-time, az/za by
 * data-title (localeCompare).
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var grid = document.querySelector('[data-tyt-blog-grid]');
    if (!grid) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-tyt-adv-card]'));
    if (!cards.length) return;

    var filterBar = document.querySelector('[data-tyt-blog-filter-bar]');
    var regionValueEl = document.querySelector('[data-tyt-blog-region-value]');
    var searchInput = document.querySelector('[data-tyt-blog-search-input]');
    var sortSelect = document.querySelector('[data-tyt-blog-sort-select]');
    var visibleCountEl = document.querySelector('[data-tyt-blog-visible-count]');
    var currentSortEl = document.querySelector('[data-tyt-blog-current-sort]');
    var noResultsEl = document.querySelector('[data-tyt-blog-no-results]');
    var clearBtn = document.querySelector('[data-tyt-blog-clear-filters]');

    var showMoreBtn = document.querySelector('[data-tyt-blog-show-more]');
    var initialVisible = parseInt(grid.getAttribute('data-tyt-archive-initial-visible') || '15', 10);
    var showMoreBatch = parseInt(grid.getAttribute('data-tyt-archive-batch') || '15', 10);

    var state = {
      region: 'all',    // 'all' or a region value
      query: '',
      sort: 'newest',   // newest | oldest | az | za
      visibleCount: initialVisible,
    };

    /* ── the series band ─────────────────────────────────────────────────
     * Series articles are DELIBERATELY absent from the archive grid:
     * tyt-blog-archive.liquid only takes articles where custom.series is
     * blank, because they render in the tyt-blog-series band instead. That
     * left them unreachable by this filter - searching "potomac" returned
     * nothing while the Potomac catch-up sat visibly on the same page. The
     * band is now filtered alongside the archive (Maud's option A,
     * 2026-09-04). */
    var seriesSection = document.querySelector('[data-tyt-series-section]');
    var seriesPanel = seriesSection && seriesSection.querySelector('[data-tyt-blog-series]');
    var seriesCards = seriesPanel
      ? Array.prototype.slice.call(seriesPanel.querySelectorAll('[data-tyt-adv-card]'))
      : [];
    var seriesPartsEl = seriesSection && seriesSection.querySelector('[data-tyt-series-parts]');
    var seriesMoreEl = seriesSection && seriesSection.querySelector('[data-tyt-series-more]');
    var seriesSaved = null;   // non-null only while a filter is running

    function cardPasses(card, tokens) {
      var cardRegion = (card.getAttribute('data-region') || '').toLowerCase();
      var cardText = (card.getAttribute('data-search-text') || '').toLowerCase();
      var regionPass = state.region === 'all' || cardRegion === state.region;
      return regionPass && tokens.every(function (t) { return cardText.indexOf(t) !== -1; });
    }

    function applySeriesFilter(tokens, filtering) {
      if (!seriesSection || !seriesCards.length) return 0;

      if (!filtering) {
        // Put the band back exactly as the reader left it.
        seriesSection.hidden = false;
        seriesCards.forEach(function (c) { c.hidden = false; });
        if (seriesMoreEl) seriesMoreEl.hidden = false;
        if (seriesSaved && seriesPanel) {
          if (seriesSaved.had) seriesPanel.setAttribute('data-collapsed', seriesSaved.val);
          else seriesPanel.removeAttribute('data-collapsed');
        }
        seriesSaved = null;
        if (seriesPartsEl) seriesPartsEl.textContent = seriesPartsEl.getAttribute('data-total');
        return 0;
      }

      // Remember the collapse state ONCE, on the way in, so clearing the
      // search does not silently re-collapse a band the reader had opened.
      if (seriesSaved === null && seriesPanel) {
        seriesSaved = {
          had: seriesPanel.hasAttribute('data-collapsed'),
          val: seriesPanel.getAttribute('data-collapsed'),
        };
      }

      var hits = 0;
      seriesCards.forEach(function (c) {
        var ok = cardPasses(c, tokens);
        c.hidden = !ok;
        if (ok) hits++;
      });

      /* Force the band open. Parts past the third carry data-tyt-series-extra
       * and are hidden by [data-collapsed="true"], which would swallow a match
       * outright - the count would say 1 and the reader would see none. The
       * "View N earlier parts" button goes too: with the band filtered there
       * is nothing left for it to reveal, and its label would be wrong. */
      if (seriesPanel) seriesPanel.setAttribute('data-collapsed', 'false');
      if (seriesMoreEl) seriesMoreEl.hidden = true;

      seriesSection.hidden = hits === 0;
      if (seriesPartsEl) {
        seriesPartsEl.textContent = hits + ' of ' + seriesPartsEl.getAttribute('data-total');
      }
      return hits;
    }

    function applyFilters() {
      var q = (state.query || '').trim().toLowerCase();
      var tokens = q ? q.split(/\s+/) : [];
      var filtering = tokens.length > 0 || state.region !== 'all';

      var matching = [];
      cards.forEach(function (card) {
        if (cardPasses(card, tokens)) {
          matching.push(card);
        } else {
          card.hidden = true;
        }
      });

      var shownCount = 0;
      matching.forEach(function (card, idx) {
        if (idx < state.visibleCount) {
          card.hidden = false;
          shownCount++;
        } else {
          card.hidden = true;
        }
      });

      var seriesHits = applySeriesFilter(tokens, filtering);

      /* The count and the empty state describe the whole page, not just the
       * archive - otherwise a search that only hits a series part would read
       * "0 interviews" and print "no interviews match" above a visible match.
       *
       * ⚠ IT COUNTS WHAT MATCHES, NOT WHAT IS CURRENTLY REVEALED. This used to
       * read `shownCount`, which is capped at state.visibleCount (15), so a
       * fresh page load overwrote the server's honest 26 with "Showing 15
       * interviews" and the reader had to press "Load older adventures" before
       * the number told the truth. Maud, 2026-09-10: "I want them to see the
       * full number."
       *
       * When nothing is filtered the series band shows all of its parts, but
       * applySeriesFilter returns 0 in that branch (it only reports HITS), so
       * the band's own total is added instead - otherwise the thirteen series
       * articles would vanish from the count on the page they are sitting on. */
      var seriesShown = filtering ? seriesHits : seriesCards.length;
      if (visibleCountEl) visibleCountEl.textContent = String(matching.length + seriesShown);
      if (noResultsEl) noResultsEl.hidden = (matching.length + seriesHits) > 0;
      if (showMoreBtn) showMoreBtn.hidden = shownCount >= matching.length;
    }

    function applySort() {
      cards.sort(function (a, b) {
        if (state.sort === 'az' || state.sort === 'za') {
          var na = (a.getAttribute('data-title') || '').toLowerCase();
          var nb = (b.getAttribute('data-title') || '').toLowerCase();
          var cmp = na.localeCompare(nb);
          return state.sort === 'za' ? -cmp : cmp;
        }
        var ta = parseInt(a.getAttribute('data-published-time') || '0', 10);
        var tb = parseInt(b.getAttribute('data-published-time') || '0', 10);
        return state.sort === 'oldest' ? (ta - tb) : (tb - ta);
      });
      cards.forEach(function (card) { grid.appendChild(card); });
      if (currentSortEl) {
        var labels = { newest: 'Newest first', oldest: 'Oldest first', az: 'A–Z', za: 'Z–A' };
        currentSortEl.textContent = labels[state.sort] || 'Newest first';
      }
      applyFilters();
    }

    // ── Region dropdown: open/close ──
    var dd = filterBar ? filterBar.querySelector('[data-tyt-cdd]') : null;
    var ddWrap = filterBar ? filterBar.querySelector('.tyt-walks-dd') : null;
    if (dd) {
      var toggle = dd.querySelector('[data-tyt-cdd-toggle]');
      var menu = dd.querySelector('[data-tyt-cdd-menu]');
      if (toggle && menu) {
        toggle.addEventListener('click', function (e) {
          e.stopPropagation();
          var open = menu.hidden;
          menu.hidden = !open;
          toggle.setAttribute('aria-expanded', String(open));
        });
        /* ⚠ THERE IS DELIBERATELY NO BLANKET stopPropagation ON THE MENU.
           One used to sit here, and it silently swallowed every region click.
           The option buttons are delegated on [data-tyt-blog-filter-bar], which
           is an ANCESTOR of this menu (button > li > ul > menu > … > filter bar),
           so stopping the bubble here meant that handler NEVER ran: the dropdown
           opened and nothing in it was selectable. It shipped that way on
           2026-06-19 and was not noticed until 2026-09-10, because custom.region
           was unset on every article — so a working click and a dead one looked
           identical. Close-on-outside-click is containment-checked instead,
           exactly like the pointerdown handler below. */
        document.addEventListener('click', function (e) {
          if (e.target && e.target.closest && e.target.closest('[data-tyt-cdd]')) return;
          menu.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
        });
        /* Touch: iOS Safari does not reliably fire a click on `document` for a tap
           that lands on a non-interactive element, so the line above can silently
           fail on a phone and leave the menu stuck open over the results. A
           pointerdown always fires.
           Guarded to non-mouse pointers, so with a mouse NOTHING changes: the click
           handler above still does the closing, on release, exactly as before.
           Containment-checked rather than trusting stopPropagation, because
           pointerdown runs BEFORE click — the toggle's own stopPropagation has not
           happened yet, so a bare close here would be undone (and the menu
           re-opened) by the click that follows. */
        document.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'mouse') return;
          if (e.target && e.target.closest && e.target.closest('[data-tyt-cdd]')) return;
          menu.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') {
            menu.hidden = true;
            toggle.setAttribute('aria-expanded', 'false');
          }
        });
      }
    }

    function closeRegionMenu() {
      if (!dd) return;
      var m = dd.querySelector('[data-tyt-cdd-menu]');
      var t = dd.querySelector('[data-tyt-cdd-toggle]');
      if (m) m.hidden = true;
      if (t) t.setAttribute('aria-expanded', 'false');
    }

    function selectRegion(value, label) {
      state.region = (value || 'all').toLowerCase();
      if (regionValueEl && label != null) regionValueEl.textContent = label;
      if (filterBar) {
        filterBar.querySelectorAll('[data-tyt-blog-region-option]').forEach(function (b) {
          var li = b.closest('.tyt-walks-dd__option');
          if (li) li.setAttribute('aria-selected', b.getAttribute('data-region') === value ? 'true' : 'false');
        });
      }
      if (ddWrap) ddWrap.classList.toggle('is-active', state.region !== 'all');
    }

    // ── Region selection (client-side) ──
    if (filterBar) {
      filterBar.addEventListener('click', function (e) {
        var opt = e.target.closest('[data-tyt-blog-region-option]');
        if (!opt) return;
        e.preventDefault();
        selectRegion(opt.getAttribute('data-region') || 'all', opt.getAttribute('data-label') || opt.textContent.trim());
        closeRegionMenu();
        applyFilters();
      });
    }

    // ── Search ──
    if (searchInput) {
      var st = null;
      searchInput.addEventListener('input', function () {
        clearTimeout(st);
        var v = searchInput.value;
        st = setTimeout(function () {
          state.query = v;
          applyFilters();
        }, 60);
      });
    }

    // ── Sort ──
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        state.sort = sortSelect.value;
        applySort();
      });
    }

    // ── Clear filters ──
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        var firstOpt = filterBar ? filterBar.querySelector('[data-tyt-blog-region-option]') : null;
        if (firstOpt) {
          selectRegion(firstOpt.getAttribute('data-region') || 'all', firstOpt.getAttribute('data-label') || firstOpt.textContent.trim());
        }
        if (searchInput) searchInput.value = '';
        state = { region: 'all', query: '', sort: state.sort, visibleCount: initialVisible };
        applyFilters();
      });
    }

    // ── Show more ──
    if (showMoreBtn) {
      showMoreBtn.addEventListener('click', function () {
        state.visibleCount = state.visibleCount + showMoreBatch;
        applyFilters();
      });
    }

    applyFilters();
    applySort();
  });
})();
